import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { env } from "@/lib/env";
import { authClient } from "@/lib/auth-client";

type ServerStatus = "checking" | "up" | "down";

interface UseServerHealthReturn {
  status: ServerStatus;
  isDown: boolean;
  checkHealth: (isManual?: boolean) => Promise<void>;
  lastChecked: Date | null;
  isManualChecking: boolean;
}

/**
 * Hook pour surveiller la santé du serveur via la route /ok
 *
 * Fonctionnalités :
 * - Check initial au chargement
 * - Auto-retry avec backoff exponentiel (React Query)
 * - Retry manuel via checkHealth()
 * - Timeout configurable (5s par défaut)
 */
export function useServerHealth(): UseServerHealthReturn {
  const [isManualChecking, setIsManualChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  // Track previous server status to detect when it becomes available again
  const [previousStatus, setPreviousStatus] = useState<ServerStatus>("checking");
  // Track slow retry count separately to avoid conflicts with React Query retry
  const [slowRetryCount, setSlowRetryCount] = useState(0);
  // Track if we're in slow retry mode (after overlay is shown)
  const [isInSlowRetryMode, setIsInSlowRetryMode] = useState(false);

  const queryClient = useQueryClient();
  const healthCheckUrl = `${env.VITE_API_BASE_URL}/ok`;

  const { data, isLoading, isError, refetch, failureCount } = useQuery({
    queryKey: ["server-health"],
    queryFn: async ({ signal }) => {
      const controller = new AbortController();

      // Timeout de 5 secondes
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Combiner les signaux d'abort
      signal?.addEventListener("abort", () => controller.abort());

      try {
        const response = await fetch(healthCheckUrl, {
          method: "GET",
          signal: controller.signal,
          // Mode cors standard pour permettre la lecture de la réponse
          //mode: "cors",
          // Pas de headers personnalisés pour rester une "simple request"
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        setLastChecked(new Date());
        return response.text();
      } catch (error) {
        clearTimeout(timeoutId);
        setLastChecked(new Date());
        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Retry rapides AVANT d'afficher l'overlay : seulement 2 tentatives
      // | 0sec | 1 sec | 2 sec | -> page d'erreur
      if (failureCount >= 2) return false;

      // Ne pas retry sur AbortError
      if (error instanceof Error && error.name === "AbortError") {
        return false;
      }

      return true;
    },
    retryDelay: (attemptIndex) => {
      // Retry rapides avant affichage : 1s, 2s
      // Pour afficher l'overlay rapidement
      return 1000 * (attemptIndex + 1); // 1s, 2s
    },
    refetchInterval: (query) => {
      // Si on a des données (serveur up), refetch toutes les 5 minutes
      if (query.state.data) {
        return 5 * 60 * 1000;
      }

      // Si le serveur est down, utiliser notre système de slow retry séparé
      if (query.state.error && isInSlowRetryMode) {
        // Exponential backoff: 4s, 8s, 16s, 32s, 64s, 128s, 256s (≈4.3min), puis 5min
        const backoffTime = Math.min(4000 * Math.pow(2, slowRetryCount), 5 * 60 * 1000);
        return backoffTime;
      }

      // Pas de refetch automatique pendant les retry rapides initiaux
      return false;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    // Configuration pour être plus agressif sur la détection d'erreurs
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  // Effect to listen for API network errors and trigger immediate health check
  useEffect(() => {
    const handleNetworkError = () => {
      // Seulement déclencher un refetch si on n'est pas déjà en mode slow retry
      // pour éviter les double checks
      if (!isInSlowRetryMode) {
        // console.log("API network error detected, triggering immediate health check");
        refetch();
      }
    };

    window.addEventListener("api-network-error", handleNetworkError);
    return () => window.removeEventListener("api-network-error", handleNetworkError);
  }, [refetch, isInSlowRetryMode]);

  // Déterminer le statut - plus agressif pour détecter les problèmes
  const status: ServerStatus = (() => {
    // Si on est en cours de chargement pour la première fois (aucun échec précédent)
    if (isLoading && !data && failureCount === 0) return "checking";

    // Si on a une erreur OU pas de données ET qu'on a eu des échecs
    if (isError || (!data && failureCount > 0)) return "down";

    // Si on a des données, le serveur est up
    if (data) return "up";

    // Par défaut, en cours de vérification
    return "checking";
  })();

  // Effect to manage slow retry mode based on server status
  useEffect(() => {
    // Si le serveur est down et qu'on a épuisé les retry rapides, passer en mode slow retry
    if (status === "down" && failureCount >= 2 && !isInSlowRetryMode) {
      console.log("Switching to slow retry mode after fast retries exhausted");
      setIsInSlowRetryMode(true);
      setSlowRetryCount(0); // Reset counter for slow retries
    }

    // Reset slow retry mode when server is back up
    else if (status === "up" && isInSlowRetryMode) {
      console.log("Server is back up, resetting slow retry mode");
      setIsInSlowRetryMode(false);
      setSlowRetryCount(0);
    }
  }, [status, failureCount, isInSlowRetryMode]);

  // Effect to increment slow retry counter after each slow retry attempt
  useEffect(() => {
    if (isInSlowRetryMode && status === "down") {
      const timer = setTimeout(() => {
        setSlowRetryCount((prev) => prev + 1);
      }, 100); // Small delay to ensure the refetch has happened

      return () => clearTimeout(timer);
    }
  }, [isInSlowRetryMode, status]); // Removed data and isError to prevent infinite loops

  // Effect to handle session cache invalidation when server becomes available again
  useEffect(() => {
    // If server was down and now it's up, refresh auth session
    if (previousStatus === "down" && status === "up") {
      console.log("Server is back up, refreshing session cache...");

      // Invalidate auth session cache to force re-check
      authClient.getSession({
        query: { disableCookieCache: true },
        fetchOptions: {
          onSuccess: (context) => {
            console.log("Session cache refreshed successfully");

            // If we have a valid session and we're on login page, redirect appropriately
            if (context?.data?.user && window.location.pathname === "/login") {
              console.log("Valid session found after server recovery, redirecting from login page");

              // Check user onboarding status to determine where to redirect
              const user = context.data.user;
              if (user.onboarding === "done") {
                window.location.href = "/dash";
              } else if (user.onboarding) {
                window.location.href = `/onboarding/${user.onboarding}`;
              } else {
                window.location.href = "/dash";
              }
            }
          },
          onError: () => {
            console.log("Session refresh failed, user may need to re-login");
          },
        },
      });

      // Also invalidate React Query cache for session-related queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          // Invalidate auth-related and API queries
          const queryKey = query.queryKey;
          return (
            queryKey.includes("session") || queryKey.includes("auth") || (Array.isArray(queryKey) && queryKey[0] === "get") // Invalidate all API queries
          );
        },
      });
    }

    // Update previous status for next comparison
    setPreviousStatus(status);
  }, [status, previousStatus, queryClient]);
  const checkHealth = useCallback(
    async (isManual = false): Promise<void> => {
      if (isManual) {
        setIsManualChecking(true);

        // Délai minimum de 400ms pour les checks manuels
        const startTime = Date.now();
        const minLoadingTime = 400;

        try {
          await refetch();

          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsed);

          if (remainingTime > 0) {
            await new Promise((resolve) => setTimeout(resolve, remainingTime));
          }
        } finally {
          setIsManualChecking(false);
        }
      } else {
        await refetch();
      }
    },
    [refetch]
  );

  return {
    status,
    isDown: status === "down",
    checkHealth,
    lastChecked,
    isManualChecking,
  };
}
